import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ItineraryItemType, Trip } from '../../types/client';
import type { ForecastDay } from '../../types/weather';
import { tripDetailEn } from '../../content/trips.en';
import { weatherEn } from '../../content/weather.en';
import { useSubscriptionFeature } from '../../contexts/SubscriptionContext';
import {
  formatCardWeekdayDate,
  portTimeLineForCard,
  type TripDayCardModel,
} from '../../utils/tripDayCards';
import { formatTempRange, resolveTripTemperatureUnit } from '../../utils/temperatureUnit';
import WeatherConditionIcon from '../weather/WeatherConditionIcon';
import CountryInfoDrawer from '../CountryInfo/CountryInfoDrawer';
import { FerryIcon, TypeIcon } from '../icons/TripIcons';
import styles from './CityVisitCard.module.css';

interface CityVisitCardProps {
  tripId: string;
  card: TripDayCardModel;
  showPlanIcons: boolean;
  canPlan: boolean;
  weather?: ForecastDay | null;
  temperatureUnit?: string | null;
  weatherLoading?: boolean;
  trip?: Trip | null;
}

const STATUS_LABEL: Record<string, string> = {
  docked: tripDetailEn.docked,
  tendered: tripDetailEn.tendered,
  cruising: tripDetailEn.cruising,
};

const CityVisitCard: React.FC<CityVisitCardProps> = ({
  tripId,
  card,
  showPlanIcons,
  canPlan,
  weather,
  temperatureUnit,
  weatherLoading = false,
  trip = null,
}) => {
  const navigate = useNavigate();
  const includesAIPlan = useSubscriptionFeature('aiPlan');
  const [countryInfoOpen, setCountryInfoOpen] = useState(false);
  const unit = resolveTripTemperatureUnit({ temperatureUnit });
  const dateLabel = formatCardWeekdayDate(card.arrivalDate);
  const timeLine = portTimeLineForCard(card);
  const dayHref =
    canPlan && card.arrivalDate
      ? `/trips/${tripId}/days/${card.arrivalDate}?cityVisitId=${encodeURIComponent(card.cityId)}`
      : undefined;

  const status =
    card.isCruisePort && card.cruisePortStatus
      ? STATUS_LABEL[card.cruisePortStatus] ?? tripDetailEn.docked
      : card.isCruisePort
        ? tripDetailEn.docked
        : null;

  const openDay = (): void => {
    if (dayHref) navigate(dayHref);
  };

  return (
    <>
      <div
        className={`${styles.card} ${dayHref ? styles.cardClickable : ''}`}
        role={dayHref ? 'link' : undefined}
        tabIndex={dayHref ? 0 : undefined}
        onClick={dayHref ? openDay : undefined}
        onKeyDown={
          dayHref
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openDay();
                }
              }
            : undefined
        }
        aria-label={dayHref ? `${card.cityName}. ${tripDetailEn.openDay}` : card.cityName}
      >
        <div className={styles.topRow}>
          <div className={styles.titleGroup}>
            <span className={styles.cityName}>{card.cityName}</span>
            {!card.isAtSea ? (
              <button
                type="button"
                className={styles.infoIcon}
                title={tripDetailEn.countryInfo ?? 'City info'}
                aria-label={tripDetailEn.countryInfo ?? 'City info'}
                onClick={(e) => {
                  e.stopPropagation();
                  setCountryInfoOpen(true);
                }}
              >
                ⓘ
              </button>
            ) : null}
            {status ? (
              <span
                className={`${styles.statusBadge} ${
                  card.cruisePortStatus === 'tendered'
                    ? styles.statusTendered
                    : card.cruisePortStatus === 'cruising'
                      ? styles.statusCruising
                      : styles.statusDocked
                }`}
              >
                <FerryIcon size={14} />
                {status}
              </span>
            ) : null}
          </div>
          <div className={styles.trailing}>
            {!card.isAtSea ? (
              <div className={styles.weatherCompact} aria-label="Weather">
                {weatherLoading ? (
                  <span className={styles.weatherLoading}>{weatherEn.loading}</span>
                ) : weather ? (
                  <>
                    <WeatherConditionIcon
                      iconName={weather.iconName}
                      dimmed={weather.isHistorical}
                      size={16}
                    />
                    <span className={styles.weatherRange}>
                      {formatTempRange(weather.highTempC, weather.lowTempC, unit)}
                    </span>
                  </>
                ) : null}
              </div>
            ) : null}
            {showPlanIcons && !card.isAtSea && canPlan && includesAIPlan ? (
              <Link
                to={`/trips/${tripId}/plan${card.arrivalDate ? `?date=${card.arrivalDate}` : ''}`}
                className={styles.planButton}
                onClick={(e) => e.stopPropagation()}
              >
                {tripDetailEn.aiPlan}
              </Link>
            ) : null}
            <span className={styles.chevron} aria-hidden>
              ›
            </span>
          </div>
        </div>

        <div className={styles.metaRow}>
          {dateLabel ? <span className={styles.date}>{dateLabel}</span> : null}
          {timeLine ? <span className={styles.time}>{timeLine}</span> : null}
        </div>

        {showPlanIcons || card.itemTypes.length > 0 ? (
          <div className={styles.iconsRow} aria-hidden>
            {(showPlanIcons
              ? (['transport', 'activity', 'meal', 'accommodation'] as ItineraryItemType[])
              : card.itemTypes
            ).map((type) => (
              <span
                key={type}
                className={`${styles.typeIcon} ${card.itemTypes.includes(type) ? styles.typeIconActive : ''}`}
              >
                <TypeIcon type={type} size={18} />
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <CountryInfoDrawer
        open={countryInfoOpen}
        onClose={() => setCountryInfoOpen(false)}
        cityName={card.cityName}
        countryISO2={card.countryISO2}
        countryName={card.countryName}
        currencyCode={card.currencyCode}
        timeZoneIdentifier={card.timeZoneIdentifier}
        tripStartDate={trip?.startDate}
        cityArrivalDate={card.arrivalDate}
        tripCurrency={trip?.currency}
        tripHomeTimeZone={trip?.homeTimeZone}
        isCruisePort={card.isCruisePort}
        coords={trip?.cityCoords?.[card.cityId] ?? null}
        dockNumber={card.dockNumber}
        portArrivalHm={card.portArrivalHm}
        portDepartureHm={card.portDepartureHm}
      />
    </>
  );
};

export default CityVisitCard;
